/**
 * Criteria Filtered Accordion Block
 *
 * Handles search, A-Z filtering, taxonomy filtering, sorting, and pagination
 * for mortgage criteria/FAQ content with accordion display
 */
import LoadingManager from '../../../FE/src/assets/javascripts/utils/LoadingManager.js';

export default class CriteriaAccordion {
  constructor(el) {
    this.$el = $(el);
    this.$wrapper = this.$el;
    this.$results = this.$el.find('[data-criteria-results]');
    this.$clearSearch = this.$el.find('[data-clear-search]');
    this.$azLetters = this.$el.find('[data-az-letter]');
    this.$clearFilters = this.$el.find('[data-clear-filters]');
    this.$paginationWrapper = this.$el.find('[data-pagination-wrapper]');
    this.$noResults = this.$el.find('.gg-criteria-accordion__no-results');
    this.$controls = this.$el.find('.gg-criteria-accordion__controls');
    this.$categoryFilter = this.$el.find(
      '.gg-criteria-accordion__filter--category',
    );
    this.$subCategoryFilter = this.$el.find(
      '.gg-criteria-accordion__filter--sub-category',
    );
    this.$topicFilter = this.$el.find('.gg-criteria-accordion__filter--topic');
    this.$sortFilter = this.$el.find('.gg-criteria-accordion__sort');
    this.$searchInfo = this.$el.find('.gg-criteria-accordion__search-info');
    this.$count = this.$el.find('.js-criteria-count');
    this.$resultText = this.$el.find('.js-criteria-result-text');
    this.$queryText = this.$el.find('.js-criteria-query');
    this.uuid = this.$results.data('uuid');
    this.itemsPerPage = this.$results.data('items-per-page') || 10;
    this.clsCriteriaWrapper = 'gg-criteria-accordion__wrapper';
    this.subcategoryMap = JSON.parse(
      this.$el.find(`.${this.clsCriteriaWrapper}`).attr('data-subcategory-map') || '{}',
    );
    // Get block settings from data attributes
    this.blockType = this.$el.find(`.${this.clsCriteriaWrapper}`).attr('data-type') || '';
    this.disableAzIndex = this.$el.find(`.${this.clsCriteriaWrapper}`).attr('data-disable-az-index') === 'true';

    // Current state from URL
    this.currentPage = this.getUrlParameter('page') || 1;
    this.currentSearch = this.getUrlParameter('keywords') || '';
    this.currentAZ = this.getUrlParameter('az') || '';
    this.currentCategory = this.getUrlParameter('category') || '';
    this.currentSubCategory = this.getUrlParameter('sub-category') || '';
    this.currentTopic = this.getUrlParameter('topic') || '';
    this.currentSort = this.getUrlParameter('sort') || '';
    this.focusmode = this.getUrlParameter('focusmode') || '';
    this.accordionid = this.getUrlParameter('accordionid') || '';

    this.isLoading = false;
  }

  init() {
    this.bindEvents();
    this.updateSubCategoryDropdown(this.currentCategory);
  }

  // Get URL parameter
  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name) || '';
  }

  // Set URL parameter
  setUrlParameter(name, value) {
    const urlParams = new URLSearchParams(window.location.search);

    // Remove parameter if empty
    if (value === '' || !value) {
      urlParams.delete(name);
    } else {
      urlParams.set(name, value);
    }

    const queryString = urlParams.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }

  // Update multiple URL parameters at once
  updateURL(params) {
    const urlParams = new URLSearchParams(window.location.search);

    Object.keys(params).forEach((key) => {
      const value = params[key];
      // Remove parameter if empty/null/undefined OR if page=1 (default page)
      if (
        value === '' ||
        value === null ||
        value === undefined ||
        (key === 'page' && (value === 1 || value === '1'))
      ) {
        urlParams.delete(key);
      } else {
        // Convert to string for URL parameter
        urlParams.set(key, String(value));
      }
    });

    const queryString = urlParams.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    window.history.pushState({}, '', newUrl);

    // Update current state
    Object.keys(params).forEach((key) => {
      const value = params[key];
      const camelKey =
        `current${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_([a-z])/g, (g) => g[1].toUpperCase())}`;
      // eslint-disable-next-line no-prototype-builtins
      if (this.hasOwnProperty(camelKey)) {
        // Keep the original value type (number or string)
        this[camelKey] =
          value === '' || value === null || value === undefined
            ? ''
            : params[key];
      }
    });
  }

  bindEvents() {
    // Listen for search from banner (gravity-news-hero)
    $(document).on('searchFromBanner', (_e, searchQuery) => {
      this.handleSearchFromBanner(searchQuery);
    });

    // Clear Search
    this.$clearSearch.on('click', () => this.handleClearSearch());

    // A-Z Index
    this.$azLetters.on('click', (e) => this.handleAZClick(e));

    // Clear Filters
    this.$clearFilters.on('click', () => this.handleClearFilters());

    // Pagination - Use event delegation for AJAX-loaded pagination
    this.$el.on(
      'click',
      '.page-numbers:not(.disabled):not(.dots):not(.current)',
      (e) => {
        // Prevent default link behavior
        e.preventDefault();
        this.handlePaginationClick(e);
      },
    );

    // Listen for filter changes from gg-select
    $(document).on('criteriaCategoryChanged', (_e, value, _label) => {
      this.currentCategory = value;
      this.updateSubCategoryDropdown(value);
      this.handleFilterChange();
    });

    $(document).on('criteriaSubCategoryChanged', (_e, value, _label) => {
      this.currentSubCategory = value;
      this.handleFilterChange();
    });

    $(document).on('criteriaTopicChanged', (_e, value, _label) => {
      this.currentTopic = value;
      this.handleFilterChange();
    });

    // Listen for sort changes from gg-select
    $(document).on('criteriaSortChanged', (_e, value, _label) => {
      this.currentSort = value;
      this.loadCriteria();
    });
  }

  handleSearchFromBanner(searchQuery) {
    // Reset ALL filters when searching
    this.currentSearch = searchQuery.trim();
    this.currentAZ = '';
    this.currentCategory = '';
    this.currentSubCategory = '';
    this.currentTopic = '';
    this.currentSort = '';
    this.currentPage = 1;
    this.focusmode = '';
    this.accordionid = '';
    // Hide controls (A-Z + Filters)
    this.$controls.hide();

    this.loadCriteria();
  }

  handleClearSearch() {
    this.currentSearch = '';
    // Will be set to default in AJAX response
    this.currentAZ = '';
    this.currentPage = 1;
    this.focusmode = '';
    this.accordionid = '';
    // Show controls (A-Z + Filters) again
    this.$controls.show();

    this.loadCriteria();
  }

  handleAZClick(e) {
    const $letter = $(e.currentTarget);

    if ($letter.prop('disabled')) {
      return;
    }

    const letter = $letter.data('az-letter');
    const isActive = $letter.hasClass('active');

    // Toggle active state
    this.$azLetters.removeClass('active');

    if (!isActive) {
      $letter.addClass('active');
      this.currentAZ = letter;
    } else {
      this.currentAZ = '';
    }

    this.currentPage = 1;

    this.loadCriteria();
  }

  handleFilterChange() {
    // Get current filter values from gg-select dropdowns
    this.currentPage = 1;

    this.loadCriteria();
  }

  handleClearFilters() {
    // Clear all filters and A-Z (will be set to default in AJAX response)
    this.currentAZ = '';
    this.currentCategory = '';
    this.currentSubCategory = '';
    this.currentTopic = '';
    this.currentSort = '';
    this.currentPage = 1;
    this.focusmode = '';
    this.accordionid = '';
    // Reset all filter dropdowns
    if (this.$categoryFilter.length && this.$categoryFilter.data('ggSelect')) {
      this.$categoryFilter.data('ggSelect').reset();
    }
    if (
      this.$subCategoryFilter.length &&
      this.$subCategoryFilter.data('ggSelect')
    ) {
      this.$subCategoryFilter.data('ggSelect').reset();
    }
    if (this.$topicFilter.length && this.$topicFilter.data('ggSelect')) {
      this.$topicFilter.data('ggSelect').reset();
    }
    if (this.$sortFilter.length && this.$sortFilter.data('ggSelect')) {
      this.$sortFilter.data('ggSelect').reset();
    }

    this.updateSubCategoryDropdown('');

    // Clear UI (will be updated by AJAX response with default letter)
    this.$azLetters.removeClass('active');

    this.loadCriteria();
  }

  handlePaginationClick(e) {
    const $target = $(e.currentTarget);

    // Check if disabled or dots
    if (
      $target.hasClass('disabled') ||
      $target.hasClass('dots') ||
      $target.attr('aria-disabled') === 'true'
    ) {
      return;
    }

    // Get page number from data attribute
    // eslint-disable-next-line radix
    let page = parseInt($target.data('page'));

    if (!page || Number.isNaN(page)) {
      // Extract page from href as fallback
      const href = $target.attr('href');
      if (href && href.includes('?')) {
        const urlParams = new URLSearchParams(href.split('?')[1]);
        // eslint-disable-next-line radix
        page = parseInt(urlParams.get('page'));
      }
    }
    this.currentPage = page;
    this.loadCriteria();
  }

  loadCriteria() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    LoadingManager.show();
    if (this.focusmode !== 'true') {
      this.$el.removeClass('focusmode');
    } else {
      this.$el.addClass('focusmode');
    }
    this.updateURL({
      keywords: this.currentSearch,
      az: this.currentAZ,
      category: this.currentCategory,
      'sub-category': this.currentSubCategory,
      topic: this.currentTopic,
      sort: this.currentSort,
      page: this.currentPage > 1 ? this.currentPage : '',
      focusmode: this.focusmode,
      accordionid: this.accordionid,
    });
    $.ajax({
      url: window.ajaxurl || '/wp-admin/admin-ajax.php',
      type: 'POST',
      dataType: 'json',
      data: {
        action: 'load_criteria_items',
        search: this.currentSearch,
        az: this.currentAZ,
        category: this.currentCategory,
        // eslint-disable-next-line camelcase
        sub_category: this.currentSubCategory,
        topic: this.currentTopic,
        type: this.blockType,
        sort: this.currentSort,
        page: this.currentPage ? this.currentPage : 1,
        // eslint-disable-next-line camelcase
        items_per_page: this.itemsPerPage,
        uuid: this.uuid,
      },
      success: (response) => {
        if (response.success) {
          // Update results
          this.$results.html(response.data.html);

          // Replace pagination HTML
          if (response.data.pagination_html) {
            this.$paginationWrapper.html(response.data.pagination_html);
            this.$paginationWrapper.show();
          } else {
            this.$paginationWrapper.hide();
          }

          // Update A-Z letters availability
          this.updateAZLetters(response.data.available_letters);

          // Update active letter from response (for default letter)
          if (response.data.active_letter) {
            this.currentAZ = response.data.active_letter;
            this.$azLetters.removeClass('active');
            this.$azLetters
              .filter(`[data-az-letter='${response.data.active_letter}']`)
              .addClass('active');
          }

          // Update URL after successful AJAX

          // Show/hide no results
          if (response.data.total === 0) {
            this.$noResults.show();
            this.$results.hide();
            this.$paginationWrapper.hide();
          } else {
            this.$noResults.hide();
            this.$results.show();
          }

          // Show/hide controls and search info based on search
          if (this.currentSearch) {
            this.$controls.hide();
            this.$searchInfo.show();

            // Update search text
            this.$count.text(response.data.total);
            this.$resultText.text(
              response.data.total !== 1 ? 'Results' : 'Result',
            );
            this.$queryText.text(this.currentSearch);
          } else {
            this.$controls.show();
            this.$searchInfo.hide();
          }

          // Update clear filters button state
          const hasActiveFilters =
            this.currentCategory ||
            this.currentSubCategory ||
            this.currentTopic
          this.$clearFilters.prop('disabled', !hasActiveFilters);

          // Scroll to top of results smoothly
          this.$wrapper[0].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        } else {
          console.error('Error loading criteria:', response);
        }
      },
      error: (error) => {
        console.error('AJAX error:', error);
      },
      complete: () => {
        this.isLoading = false;
        LoadingManager.hide();
      },
    });
  }

  updateSubCategoryDropdown(categorySlug) {
    if (!this.$subCategoryFilter.length) {
      return;
    }

    const ggSelect = this.$subCategoryFilter.data('ggSelect');
    const allowedSlugs = this.subcategoryMap[categorySlug] || [];

    this.$subCategoryFilter
      .find('.gg-select__option')
      .each((_i, el) => {
        const $opt = $(el);
        const val = String($opt.data('value'));
        $opt.toggle(val === '' || allowedSlugs.includes(val));
      });

    const hasChildren = !!categorySlug && allowedSlugs.length > 0;
    this.$subCategoryFilter.toggleClass('disabled', !hasChildren);
    this.$subCategoryFilter
      .find('.gg-select__button')
      .prop('disabled', !hasChildren);

    if (ggSelect) {
      ggSelect.reset();
    }
    this.currentSubCategory = '';
  }

  updateAZLetters(availableLetters) {
    this.$azLetters.each((_i, letter) => {
      const $letter = $(letter);
      const letterValue = $letter.data('az-letter');
      const isAvailable = availableLetters.includes(letterValue);
      $letter.prop('disabled', !isAvailable);
    });
  }
}

// Initialize on page load
if (typeof window.acf === 'undefined') {
  $(document).ready(() => {
    $('.gg-criteria-accordion').each((_index, el) => {
      new CriteriaAccordion(el).init();
    });
  });
} else {
  // eslint-disable-next-line
  acf.addAction(
    'render_block_preview/type=acf/gravity-criteria-filtered-accordion',
    ($el) => {
      $el.find('.gg-criteria-accordion').each((_index, el) => {
        new CriteriaAccordion(el).init();
      });
    },
  );
}
